import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowLeft, Calculator, Check, ChevronDown, ChevronRight, FileText, History, RotateCcw, Settings2, Tag } from 'lucide-react';
import { VersionMeta } from './types';
import { SOURCE_EN, fmtDate, modelDisplay } from './utils';
import './editor-project-menu.css';

interface Props {
  canRestore?: boolean;
  historyOnly?: boolean;
  hideTrigger?: boolean;
  openRequest?: number;
  versions: VersionMeta[];
  onRestore: (v: VersionMeta) => void;
  canEstimate: boolean;
  canPricing: boolean;
  canProposal: boolean;
  running: boolean;
  onEstimate: () => void;
  onPricing: () => void;
  onProposal: () => void;
}

/** Presentation only: visibility and callbacks stay owned by EditorView. */
export default function EditorProjectMenu({
  canRestore = true, historyOnly = false, hideTrigger = false, openRequest = 0, versions, onRestore, canEstimate, canPricing, canProposal, running,
  onEstimate, onPricing, onProposal,
}: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'actions' | 'versions'>('actions');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusEdge = useRef<'first' | 'last'>('first');
  const panelId = useId();
  const titleId = useId();
  useEffect(() => { if (openRequest) { setView('versions'); setOpen(true); } }, [openRequest]);
  const sorted = (versions || []).slice().sort((a, b) => (b.seq || 0) - (a.seq || 0));

  const focusButton = (edge: 'first' | 'last') => {
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
    if (buttons?.length) buttons[edge === 'last' ? buttons.length - 1 : 0].focus();
  };

  const close = (returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    focusButton(focusEdge.current);
    focusEdge.current = 'first';
  }, [open, view]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onOutside);
    return () => document.removeEventListener('pointerdown', onOutside);
  }, [open]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const fromTrigger = event.target === triggerRef.current;
    if (fromTrigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      focusEdge.current = event.key === 'ArrowUp' ? 'last' : 'first';
      if (open) focusButton(focusEdge.current);
      else {
        setView(historyOnly ? 'versions' : 'actions');
        setOpen(true);
      }
      return;
    }
    if (!open || fromTrigger) return;
    const buttons = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
    if (!buttons.length) return;
    event.preventDefault();
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next].focus();
  };

  const launch = (callback: () => void) => {
    close(true);
    callback();
  };

  return (
    <div
      className={'arch-project-menu' + (historyOnly ? ' arch-project-menu-history' : '') + (hideTrigger ? ' stage-history-popover' : '')}
      ref={rootRef}
      onKeyDown={onKeyDown}
      onBlurCapture={(event) => {
        // Tab remains natural; close only when focus leaves the entire disclosure.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        hidden={hideTrigger}
        type="button"
        className="arch-btn arch-btn-outline arch-project-menu-trigger"
        ref={triggerRef}
        aria-label={historyOnly ? 'Architecture versions' : 'Project menu'}
        title={historyOnly ? 'Architecture version history' : 'Project menu'}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (open) close();
          else {
            focusEdge.current = 'first';
            setView(historyOnly ? 'versions' : 'actions');
            setOpen(true);
          }
        }}
      >
        {historyOnly ? <History size={16} aria-hidden="true" /> : <Settings2 size={16} className="arch-project-menu-settings" aria-hidden="true" />}
        <span className="arch-project-menu-full-label">{historyOnly ? 'Versions' : 'Project menu'}</span>
        <span className="arch-project-menu-short-label">{historyOnly ? 'Versions' : 'Menu'}</span>
        <ChevronDown size={14} className="arch-project-menu-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div id={panelId} ref={panelRef} className="arch-project-menu-panel" role="region" aria-labelledby={titleId}>
          <div className="arch-project-menu-heading">
            {view === 'versions' && !historyOnly && (
              <button type="button" className="arch-project-menu-back" aria-label="Back to project menu" onClick={() => setView('actions')}>
                <ArrowLeft size={16} aria-hidden="true" />
              </button>
            )}
            <span id={titleId}>{view === 'versions' ? 'Version history' : 'Project workspace'}</span>
            {view === 'versions' && <span className="arch-project-menu-count">{sorted.length}</span>}
          </div>

          {view === 'actions' ? (
            <>
              {(canEstimate || canPricing || canProposal) && (
                <div className="arch-project-menu-actions">
                  <div className="arch-project-menu-label">Documents & planning</div>
                  {canEstimate && (
                    <button type="button" className="arch-project-menu-action" disabled={running} onClick={() => launch(onEstimate)}>
                      <span className="arch-project-menu-icon"><Calculator size={18} aria-hidden="true" /></span>
                      <span className="arch-project-menu-copy"><strong>Estimate</strong><small>Review scope, effort and timeline</small></span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  )}
                  {canPricing && (
                    <button type="button" className="arch-project-menu-action" disabled={running} onClick={() => launch(onPricing)}>
                      <span className="arch-project-menu-icon"><Tag size={18} aria-hidden="true" /></span>
                      <span className="arch-project-menu-copy"><strong>Pricing</strong><small>Manage the project price sheet</small></span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  )}
                  {canProposal && (
                    <button type="button" className="arch-project-menu-action" disabled={running} onClick={() => launch(onProposal)}>
                      <span className="arch-project-menu-icon"><FileText size={18} aria-hidden="true" /></span>
                      <span className="arch-project-menu-copy"><strong>Proposal</strong><small>Prepare the client proposal</small></span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
              <div className="arch-project-menu-history-action">
                <button type="button" className="arch-project-menu-action" onClick={() => setView('versions')}>
                  <span className="arch-project-menu-icon"><History size={18} aria-hidden="true" /></span>
                  <span className="arch-project-menu-copy"><strong>Version history</strong><small>View and restore saved architecture</small></span>
                  <ChevronRight size={14} aria-hidden="true" />
                </button>
              </div>
            </>
          ) : (
            <div className="arch-project-menu-versions">
              {!sorted.length ? (
                <div className="arch-project-menu-empty">
                  <History size={24} aria-hidden="true" />
                  <strong>No versions yet</strong>
                  <span>Saved architecture versions will appear here.</span>
                </div>
              ) : sorted.map((version, index) => {
                const model = modelDisplay(version.model);
                return (
                  <div className="arch-project-menu-version" key={version.id || index}>
                    <div className="arch-project-menu-version-details">
                      <div className="arch-project-menu-version-title">
                        <strong>{'v' + version.seq}</strong>
                        <span>{version.edit_mode === 'wording' ? 'Wording edit' : version.edit_mode === 'structural' ? 'Structural edit' : SOURCE_EN[version.source] || version.source || ''}</span>
                      </div>
                      <div className="arch-project-menu-version-meta">
                        {fmtDate(version.created_at)}{version.author_name ? ' · ' + version.author_name : ''}{model ? ' · ' + model : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="arch-project-menu-restore"
                      disabled={index === 0 || !canRestore}
                      aria-label={index === 0 ? 'Current version ' + version.seq : 'Restore version ' + version.seq}
                      onClick={() => {
                        if (canRestore && index !== 0) launch(() => onRestore(version));
                      }}
                    >
                      {index === 0 ? <Check size={12} aria-hidden="true" /> : <RotateCcw size={12} aria-hidden="true" />}
                      {index === 0 ? 'Current' : 'Restore'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
